import { DropdownMenu, Icon, IconButton } from '@kontentino/ui';
import React, { FC } from 'react';
import { useCommentsSummary } from 'app/modules/posts/modules/post/components/postCommunication/comments/hooks/useCommentsSummary';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons/faEllipsisVertical';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { useUpdateApprovalPostStatus } from 'app/modules/posts/modules/detail/hooks/useUpdateApprovalPostStatus';
import { usePostCommunicationContext } from 'app/modules/posts/modules/post/components/postCommunication/PostCommunicationProvider';
import { usePostQuery } from 'app/modules/posts/modules/post/hooks/usePostQuery';
import { STATUS } from '@kontentino/kontentino-constants/Posts';

const CLIENT_DECISION_STATUSES: number[] = [
  STATUS.AT_CLIENT,
  STATUS.APPROVED,
  STATUS.REJECTED_BY_CLIENT,
];

type Props = {
  className?: string;
  buttonSize?: 'small' | 'medium';
  buttonVariant?: 'ghost' | 'secondary';
};

const CommentsTextEditorChangeDecisionClientApprovalDropdown: FC<Props> = ({
  buttonSize = 'medium',
  buttonVariant = 'secondary',
  ...props
}) => {
  const { data } = useCommentsSummary();
  const { t } = useTranslation();
  const { postId, resetCommentAction, initReworkCommentAction } =
    usePostCommunicationContext();
  const { postDetail } = usePostQuery(postId);
  const updateApprovalStatus = useUpdateApprovalPostStatus({
    postId,
  });

  const postStatus = postDetail.data?.status;
  const canChangeClientDecision =
    postStatus !== undefined && CLIENT_DECISION_STATUSES.includes(postStatus);

  if (
    !data?.lastDecision ||
    data.pendingApprovalRequest ||
    !canChangeClientDecision
  ) {
    return null;
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton
          data-name="comments_text-editor_client-change-decision"
          size={buttonSize}
          className={clsx('tw-rounded-full', props.className)}
          icon={<Icon icon={faEllipsisVertical} />}
          variant={buttonVariant}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content side="top" align="start" className="tw-z-10">
        {data.lastDecision.type === 'client_approved' && (
          <DropdownMenu.Item
            data-name="comments_text-editor_client-reject"
            onSelect={() => initReworkCommentAction('client')}
          >
            <Icon icon={faXmark} />
            <span>{t('commentsWorkflowActions.requestRework')}</span>
          </DropdownMenu.Item>
        )}
        {data.lastDecision.type === 'client_rejected' && (
          <DropdownMenu.Item
            data-name="comments_text-editor_client-approve"
            onSelect={() => {
              resetCommentAction();
              updateApprovalStatus.updateThroughComment.mutate({
                thread: 'client',
                action: `client_approval`,
                text: t('commentsWorkflowMessages.approve'),
              });
            }}
          >
            <Icon icon={faCheck} />
            <span>{t('commentsWorkflowActions.clientApprove')}</span>
          </DropdownMenu.Item>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default CommentsTextEditorChangeDecisionClientApprovalDropdown;
